import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { contactDetailApi, type ContactAnchorType, type ContactDetailDto } from '@/api/contactDetail'
import { leadsApi } from '@/api/leads'
import { audienceApi } from '@/api/audience'
import { routes } from '@/utils/routes'
import { useEnrichmentEvent } from '@/hooks/useEnrichmentEvent'
import { useToast } from '@/hooks/useToast'
import { PersonAvatar } from '@/components/common/Avatar'
import { SignalBars } from '@/components/common/SignalBars'
import { CopyButton } from '@/components/common/CopyButton'
import { ConsentChip, Icon } from './contactShared'
import { relativeTime, ICONS, sessionDisplayTitle } from './contactFormat'

interface Props {
  anchorType: ContactAnchorType
  anchorId: string
  clientCompanyId: string
  workspaceId: string
  /** Absent => cross-campaign (workspace) scope: view-only, opened from the workspace leads grid. */
  campaignId?: string
  webId?: string
  onClose: () => void
}

/**
 * Trimmed peek fly-out (ORB-383) — fast in-list triage. It mirrors the full page's furthest-along-facet
 * logic but stays light: identity, consent, a slim measure line, a few recent activities, a couple of
 * quick actions, and a prominent "Open full profile". Deliberate work (edit / merge / delete) lives on
 * the full page, not here.
 */
export function ContactPeekFlyout({ anchorType, anchorId, clientCompanyId, workspaceId, campaignId, webId, onClose }: Props) {
  const { route } = useLocation()
  const { showError } = useToast()
  const [detail, setDetail] = useState<ContactDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [enriching, setEnriching] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    setError(null)
    const ctrl = new AbortController()
    // Workspace (cross-campaign) scope takes either anchor since ORB-623 — see ContactDetailPage.
    const request = campaignId
      ? contactDetailApi.getDetail(workspaceId, campaignId, anchorType, anchorId, webId, ctrl.signal)
      : contactDetailApi.getWorkspaceDetail(workspaceId, anchorType, anchorId, ctrl.signal)
    request
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setDetail(res.data)
        else setError(res.error || 'Contact not found')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [workspaceId, campaignId, anchorType, anchorId, webId, refreshKey])

  // Live refresh if this contact's enrichment completes while the peek is open.
  useEnrichmentEvent((evt) => {
    const matches =
      (anchorType === 'lead' && evt.leadId === anchorId) ||
      (anchorType === 'audience' && evt.fileImportRowId === anchorId) ||
      (!!detail?.ContactId && evt.contactId === detail.ContactId)
    if (matches) setRefreshKey(k => k + 1)
  })

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const openFullProfile = () => {
    const scoped = routes.scoped(clientCompanyId, workspaceId)
    route(
      !campaignId
        ? scoped.workspaceContact(anchorType, anchorId)
        : webId
          ? scoped.webContact(campaignId, webId, anchorType, anchorId)
          : scoped.campaignContact(campaignId, anchorType, anchorId)
    )
  }

  const enrich = async () => {
    if (!detail || enriching) return
    setEnriching(true)
    const res = !campaignId
      ? { error: 'Enrichment is available from a campaign-scoped view' }
      : anchorType === 'lead' && detail.LeadId
        ? await leadsApi.reEnrich(workspaceId, campaignId, detail.LeadId)
        : detail.FileImportRowId
          ? await audienceApi.reEnrich(clientCompanyId, workspaceId, campaignId, detail.FileImportRowId)
          : { error: 'No anchor to enrich' }
    setEnriching(false)
    if (res.error) { showError('Enrichment failed', res.error); return }
    setDetail(d => d ? { ...d, Enrichment: { ...d.Enrichment, Status: 'Pending' } } : d)
  }

  const m = detail?.Measures
  const recent = detail?.RecentActivity.slice(0, 4) ?? []

  return (
    <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
      <div class="flex-1 bg-slate-900/40" onClick={onClose} />
      <aside class="w-full sm:w-[420px] ml-auto h-full overflow-y-auto border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl">
        <div class="p-5">
          {loading && !detail && <div class="text-sm text-slate-500 dark:text-slate-400">Loading…</div>}
          {error && !detail && <div class="text-sm text-red-600 dark:text-red-400">{error}</div>}

          {detail && (
            <>
              <div class="flex items-start gap-3">
                <PersonAvatar firstName={detail.FirstName} lastName={detail.LastName} fullName={detail.FullName} profilePicUrl={detail.ProfilePicUrl} size="lg" />
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <h2 class="truncate text-base font-semibold tracking-tight text-slate-900 dark:text-white">{detail.FullName || detail.Email || 'Contact'}</h2>
                    <span class="shrink-0 text-xs"><ConsentChip state={detail.Consent.State} /></span>
                  </div>
                  <p class="mt-0.5 truncate text-sm text-slate-500 dark:text-slate-400">
                    {[detail.JobTitle, detail.CompanyName].filter(Boolean).join(' · ') || '—'}
                  </p>
                  {detail.Email && (
                    <p class="mt-0.5 flex items-center gap-1 text-sm">
                      <span class="truncate text-slate-600 dark:text-slate-300">{detail.Email}</span>
                      <CopyButton text={detail.Email} variant="default" size="sm" />
                    </p>
                  )}
                </div>
                <button type="button" onClick={onClose} class="grid h-7 w-7 shrink-0 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800" aria-label="Close">
                  <Icon path="M18 6 6 18M6 6l12 12" size={16} />
                </button>
              </div>

              {/* adaptive emphasis */}
              {!detail.IsAudienceOnly ? (
                <div class="mt-4 flex items-center gap-2.5">
                  <SignalBars score={detail.SignalScore ?? 0} class="flex items-end h-5 w-9 gap-0.5" />
                </div>
              ) : (
                <div class="mt-4 space-y-1">
                  <p class="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                    <Icon path={ICONS.target} />Targeted{detail.TargetingListName ? <> · <span class="font-medium text-slate-800 dark:text-slate-200">{detail.TargetingListName}</span></> : null}
                  </p>
                  {!detail.Enrichment.EnrichedAt && <p class="text-sm text-slate-500 dark:text-slate-400">Not enriched yet</p>}
                </div>
              )}

              {/* slim measure line */}
              {m && (m.EmailsSent > 0 || m.Sessions > 0 || m.LastSeenUtc) ? (
                <div class="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-y border-slate-100 dark:border-slate-800/60 py-3 text-sm">
                  {m.EmailsSent > 0 && <span><span class="font-semibold tabular-nums text-slate-900 dark:text-white">{m.EmailsSent}</span> <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">emails</span></span>}
                  {m.OpenRatePercent != null && <span><span class="font-semibold tabular-nums text-slate-900 dark:text-white">{m.OpenRatePercent}%</span> <span class="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">open</span></span>}
                  {m.Sessions > 0 && <span class="text-slate-600 dark:text-slate-300">{m.Sessions} session{m.Sessions === 1 ? '' : 's'}</span>}
                  {m.LastSeenUtc && <span class="text-slate-500 dark:text-slate-400">seen {relativeTime(m.LastSeenUtc)}</span>}
                </div>
              ) : null}

              {/* recent activity (trimmed, no nesting) */}
              <div class="mt-4">
                <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Recent activity</h3>
                {recent.length > 0 ? (
                  <ul class="space-y-2.5">
                    {recent.map((a, i) => (
                      <li key={i} class="flex items-start justify-between gap-3 text-sm">
                        <span>
                          <span class="font-medium text-slate-900 dark:text-white">{sessionDisplayTitle(a)}</span>
                          {a.Subtitle && <span class="text-slate-600 dark:text-slate-300"> {a.Subtitle}</span>}
                        </span>
                        <time class="shrink-0 text-xs text-slate-400 dark:text-slate-500 tabular-nums">{relativeTime(a.OccurredAt)}</time>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div class="rounded-lg border border-dashed border-slate-200 dark:border-slate-700 px-4 py-5 text-center text-sm text-slate-500 dark:text-slate-400">No activity yet</div>
                )}
              </div>

              {/* quick actions */}
              <div class="mt-4 flex flex-wrap gap-2">
                {!detail.IsAudienceOnly ? (
                  <button type="button" disabled title="Coming soon" class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-400 dark:text-slate-500 cursor-not-allowed">
                    <Icon path={ICONS.mail} size={13} />Compose <span class="rounded bg-slate-100 dark:bg-slate-800 px-1 text-[9px] font-semibold uppercase text-slate-400 dark:text-slate-500">Soon</span>
                  </button>
                ) : campaignId ? (
                  <button type="button" onClick={enrich} disabled={enriching} class="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-60">
                    <Icon path={ICONS.sparkle} size={13} />{enriching ? 'Enriching…' : 'Enrich contact'}
                  </button>
                ) : null}
                {!detail.IsAudienceOnly && campaignId && (
                  <button type="button" onClick={enrich} disabled={enriching} class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-60">
                    <Icon path={ICONS.sparkle} size={13} />{enriching ? 'Enriching…' : 'Re-enrich'}
                  </button>
                )}
                {detail.Email && <CopyButton text={detail.Email} label="Copy email" variant="default" size="sm" />}
              </div>

              <button
                type="button"
                onClick={openFullProfile}
                class="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-600 px-3 py-2.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
              >
                Open full profile <Icon path={ICONS.arrowRight} size={15} />
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
